import React, { Component, Fragment } from 'react';
import {
  Upload,
  Button,
  Input,
  InputNumber,
  Select,
  Form,
  Table,
  Space,
  Spin,
  Checkbox,
} from 'antd';
import {
  ArrowLeftOutlined,
  EyeTwoTone,
  EyeInvisibleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import styles from './sa-feed.module.sass';
import {
  FEED_AUTHORIZATION,
  FEED_THREAT_TYPE,
  FEED_TYPES,
} from '../../../../redux/constants/feed';
import { STATUS } from '../../../../redux/constants';
import TagSelect from '../../../../shared-components/TagSelect';
import {
  showNotification,
  simpleErrorNotification,
  successNotification,
} from '../../../../shared-components/Notifications';

class FeedDetails extends Component {
  constructor(props) {
    super(props);
    this.formRef = React.createRef();
  }
  state = {
    isNew: !this.props.feed,
    initialValues: null,
    showInitialChoiceStep: true,
    showFileUploadStep: false,
    showAuthStep: false,
    showMappingStep: false,
    showFileUploadMappingStep: false,
    missingColumns: [],
    columnMap: {},
    selectedFile: {},
    tag: null,
  };

  componentDidMount() {
    const { feed } = this.props;
    this.props.resetFeedStatus();
    this.props.loadFeeds();

    const defaultValues = {
      // url: 'https://feodotracker.abuse.ch/downloads/ipblocklist.csv',
      authorization: FEED_AUTHORIZATION.NOT_REQUIRED,
      type: FEED_TYPES.CSV,
      threatType: FEED_THREAT_TYPE.IP,
    };

    if (!feed) {
      this.setState({
        initialValues: defaultValues,
        authorization: defaultValues.authorization,
      });
      return;
    } else {
      if (feed) {
        if (feed?.data_source === 'MANUAL') {
          this.setState({
            showInitialChoiceStep: false,
            showFileUploadStep: true,
          });
        } else if (feed?.data_source === 'URL')
          this.setState({
            showInitialChoiceStep: false,
            showAuthStep: true,
          });
      }
    }

    const authorization = feed.auth_config
      ? feed.auth_config.type
      : FEED_AUTHORIZATION.NOT_REQUIRED;
    const columnMap = Object.keys(feed.column_mapping).reduce(
      (acc, key) => ({
        ...acc,
        [feed.column_mapping[key].order]: {
          name: key,
          mapping: feed.column_mapping[key].name,
        },
      }),
      {},
    );

    this.setState({
      initialValues: {
        url: feed.display_resource_url,
        authorization,
        type: feed.resource_type,
        feedName: feed.feed_name,
        vendor: feed.feed_vendor,
        threatType: feed.threat_type,
      },
      authorization,
      columnMap,
    });
  }

  dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  handleAuthChange = (authorization) => {
    this.setState({ authorization });
  };

  handleAuthFormSubmit = (values) => {
    const { initialValues } = this.state;
    this.setState({
      showAuthStep: false,
      showMappingStep: true,
      initialValues: { ...initialValues, ...values },
    });
    this.props.consumeFeedData(values);
  };

  handleFileUploadFormSubmit = (values) => {
    const uploadObject = {
      ...this.state.selectedFile,
      //resource_type: values.type,
      threat_type: values.threatType,
      auth_config: null,
    };

    const { initialValues } = this.state;
    this.setState({
      showAuthStep: false,
      showFileUploadMappingStep: true,
      showFileUploadStep: false,
      initialValues: { ...initialValues, ...values },
    });
    this.props.consumeFileData(uploadObject);
  };

  handleTestClick = () => {
    const { authorization } = this.state;
    const form = this.formRef.current;
    if (!form) return;

    const url = form.getFieldValue('url');
    const username = form.getFieldValue('username');
    const password = form.getFieldValue('password');
    const token = form.getFieldValue('token');

    this.props.verifyAuth({
      url,
      username,
      password,
      token,
      authorization,
    });
  };

  handleFinalFormSubmit = (values) => {
    const { columnMap, initialValues, isNew } = this.state;
    const { feedTable, feed } = this.props;
    const rows = feedTable.rows;
    const firstRow = rows[0];
    const requiredColumns = [];
    const columnMapValues = Object.values(columnMap);

    const missingColumns = [];
    for (const col of requiredColumns) {
      if (!columnMapValues.includes(col)) {
        missingColumns.push(col);
      }
    }
    this.setState({ missingColumns });
    if (missingColumns.length) return;

    const columnMapData = {};
    Object.keys(columnMap).forEach((col) => {
      const mappingColumn = columnMap[col];
      columnMapData[mappingColumn.name] = {
        name: mappingColumn.mapping,
        order: +col,
      };
    });
    const data = {
      feed_name: values.feedName,
      feed_vendor: values.vendor,
      num_of_cols: firstRow.length,
      //resource_type: initialValues.type,
      threat_type: initialValues.threatType,
      resource_url: initialValues.url,
      description: values.description,
      is_global: values.is_global,
      auth_config:
        initialValues.authorization === FEED_AUTHORIZATION.NOT_REQUIRED
          ? null
          : {
              type: initialValues.authorization,
              username: initialValues.username,
              password: initialValues.password,
              token: initialValues.token,
            },
      column_mapping: columnMapData,
      ...(values.feedCustomScore && {
        custom_score: values.feedCustomScore ?? '',
      }),
      ...(this.state.tag && { tags: [this.state.tag] }),
    };

    if (isNew) {
      this.props.createGlobalFeed(data);
      this.props.handleDetailsClose();
    } else {
      data.uuid = feed.uuid;
      this.props.updateGlobalFeed(data);
      this.props.handleDetailsClose();
    }
  };

  handleFinalFileUploadFormSubmit = (values) => {
    const { columnMap, initialValues, isNew, selectedFile } = this.state;
    const { feed } = this.props;
    const requiredColumns = [];
    const columnMapValues = Object.values(columnMap);

    const missingColumns = [];
    for (const col of requiredColumns) {
      if (!columnMapValues.includes(col)) {
        missingColumns.push(col);
      }
    }
    this.setState({ missingColumns });
    if (missingColumns.length) return;

    const columnMapData = {};
    Object.keys(columnMap).forEach((col) => {
      const mappingColumn = columnMap[col];
      columnMapData[mappingColumn.name] = {
        name: mappingColumn.mapping,
        order: +col,
      };
    });

    const data = {
      name: values.feedName,
      is_user_created: true,
      cost_per_month: values.cost_per_month,
      custom_score: parseInt(values.custom_score),
      trust_level: values.trust_level,
      is_global: values.is_global,
      vendor: values.vendor,
      vendor_description: values.vendor_description,
      threat_duration: parseInt(values.threat_duration),
      type: initialValues.threatType.toLowerCase(),
      //resource_type: initialValues.type,
      threat_type: initialValues.threatType.toLowerCase(),
      column_mapping: columnMapData,
      feed_base64: selectedFile.resource_base64,
      ...(this.state.tag && { tags: [this.state.tag] }),
    };

    if (isNew) {
      //this.props.createGlobalFeed(data);
      this.props.createVirtualFeed(data);
      this.props.loadFeeds();
      this.props.handleDetailsClose();
    } else {
      data.feed_uuid = feed.uuid;
      //this.props.updateGlobalFeed(data);
      this.props.updateVirtualFeed(data);
      this.props.loadFeeds();
      this.props.handleDetailsClose();
    }
  };

  handleColumnChange = (value, index, originalColumns) => {
    const columnMap = { ...this.state.columnMap };
    if (!value) {
      delete columnMap[index];
    } else {
      const mappedValue = { name: value, mapping: value };
      if (originalColumns && index < originalColumns.length) {
        mappedValue.mapping = originalColumns[index];
      }
      columnMap[index] = mappedValue;
    }
    this.setState({ columnMap });
  };

  getData = () => {
    const { feedTable } = this.props;
    const rows = feedTable.rows.slice(0, 10);

    const data = rows.map((row) => {
      const item = {};
      row.forEach((text, index) => {
        if (typeof text === 'object' && text !== null) {
          item[index] = text?.date;
        } else {
          item[index] = text;
        }
      });
      return item;
    });

    return data;
  };

  getColumns = () => {
    const { feedTable, feedItemFields } = this.props;
    const { columnMap } = this.state;
    const rows = feedTable?.rows;
    const firstRow = rows[0];
    const hasHeader = feedTable?.has_header;
    let originalColumns = [];
    if (hasHeader) {
      originalColumns = firstRow;
    }

    const columns = firstRow?.map((text, index, cols) => {
      const col = {
        dataIndex: index,
        key: index,
        render: (text) => <span>{text}</span>,
        title: (filters, sortOrder) => (
          <Select
            style={{ width: 'fit-content', minWidth: 150 }}
            defaultActiveFirstOption
            options={feedItemFields}
            value={columnMap[index]?.name}
            onChange={(value) =>
              this.handleColumnChange(value, index, originalColumns)
            }
          />
        ),
      };
      return col;
    });

    return columns;
  };

  handleBackClick = () => {
    this.props.onCancel();
    //this.props.feed = null;
    //history.push('/feed');
  };

  renderInitialChoiceStep = () => {
    return (
      <div>
        <div className="threatRankingBackButton" onClick={this.handleBackClick}>
          <span className={styles.back_button}>
            <ArrowLeftOutlined /> Back
          </span>
        </div>
        <Button
          style={{ marginRight: '10px' }}
          onClick={() =>
            this.setState({
              showFileUploadStep: true,
              showInitialChoiceStep: false,
            })
          }
        >
          Upload a File
        </Button>
        <Button
          onClick={() =>
            this.setState({ showAuthStep: true, showInitialChoiceStep: false })
          }
        >
          New Feed Consumer
        </Button>
      </div>
    );
  };

  getBase64(file, callback) {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
      callback({ resource_base64: reader.result.replace(/^.+?;base64,/, '') }),
    );
    reader.readAsDataURL(file);
  }

  handleFileChange = (info) => {
    //const isJpgOrPng =
    // info.file.type === 'image/jpeg' || info.file.type === 'image/png';
    //if (!isJpgOrPng) {
    //  message.error('You can only upload CSV/TXT/JSON file!');
    // }
    //if (isJpgOrPng) {

    this.getBase64(info.file.originFileObj, this.fileUpload);
    //}
  };

  fileUpload = (fileObject) => {
    //if (ableToUpload) {
    //this.props.consumeFeedData()
    this.setState({ selectedFile: fileObject });
    //props.uploadPhoto(fileObject);
    // }
  };

  renderFileUploadStep = () => {
    const { isNew, initialValues } = this.state;
    const { testStatus, message } = this.props;
    const hasError = testStatus === STATUS.ERROR;

    if (!initialValues) return null;
    return (
      <div>
        <h1>{isNew ? 'New File Upload' : 'Edit File Upload'}</h1>

        <Form
          name="basic"
          autoComplete="off"
          initialValues={initialValues}
          onFinish={this.handleFileUploadFormSubmit}
          className={styles['auth-form']}
          ref={this.formRef}
          // onFinishFailed={onFinishFailed}
        >
          <Form.Item label="File Upload (Max. 1)" name="fileUpload">
            <Upload
              maxCount={1}
              customRequest={this.dummyRequest}
              onChange={this.handleFileChange}
            >
              <Button icon={<UploadOutlined />}>Click to Browse</Button>
            </Upload>
          </Form.Item>

          {message &&
            (hasError
              ? showNotification(
                  simpleErrorNotification({
                    statusCode: 400,
                    message: message,
                  }),
                )
              : showNotification(
                  successNotification({ statusCode: 200, message: message }),
                ))}

          {/* <Form.Item
            label="Type"
            name="type"
            rules={[{ required: false, message: 'Type is required' }]}
          >
            <Select>
              <Select.Option value={FEED_TYPES.CSV}>CSV</Select.Option>
              <Select.Option value={FEED_TYPES.JSON}>JSON</Select.Option>
              <Select.Option value={FEED_TYPES.TXT}>TXT</Select.Option>
              <Select.Option value={FEED_TYPES.STIX}>STIX</Select.Option>
            </Select>
          </Form.Item> */}

          <Form.Item
            label="Threat Type"
            name="threatType"
            rules={[{ required: false, message: 'Threat Type is required' }]}
          >
            <Select>
              <Select.Option value={FEED_THREAT_TYPE.IP}>IP</Select.Option>
              <Select.Option value={FEED_THREAT_TYPE.DOMAIN}>
                Domain
              </Select.Option>
              <Select.Option value={FEED_THREAT_TYPE.URL}>URL</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item className={styles.buttons}>
            <Space>
              <Button onClick={this.props.onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Consume
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    );
  };

  renderAuthStep = () => {
    const { isNew, initialValues, authorization } = this.state;
    const { testStatus, message } = this.props;
    const hasError = testStatus === STATUS.ERROR;

    if (!initialValues) return null;
    return (
      <div>
        <h1>
          {isNew ? 'New Custom Feed Consumer' : 'Edit Custom Feed Consumer'}
        </h1>

        <Form
          name="basic"
          autoComplete="off"
          initialValues={initialValues}
          onFinish={this.handleAuthFormSubmit}
          className={styles['auth-form']}
          ref={this.formRef}
          // onFinishFailed={onFinishFailed}
        >
          <Form.Item
            label="URL"
            name="url"
            rules={[{ required: true, message: 'URL is required' }]}
          >
            <Input type="url" />
          </Form.Item>

          <Form.Item label="Authorization" name="authorization">
            <Select onChange={this.handleAuthChange}>
              <Select.Option value={FEED_AUTHORIZATION.NOT_REQUIRED}>
                Not Required
              </Select.Option>
              <Select.Option value={FEED_AUTHORIZATION.BASIC}>
                Basic
              </Select.Option>
              <Select.Option value={FEED_AUTHORIZATION.TOKEN}>
                Token
              </Select.Option>
            </Select>
          </Form.Item>

          {authorization === FEED_AUTHORIZATION.BASIC && (
            <Fragment>
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: 'Username is required' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: 'Password is required' }]}
              >
                <Input.Password
                  iconRender={(visible) =>
                    visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                  }
                />
              </Form.Item>
            </Fragment>
          )}

          {authorization === FEED_AUTHORIZATION.TOKEN && (
            <Form.Item
              label="Token"
              name="token"
              rules={[{ required: true, message: 'Token is required' }]}
            >
              <Input />
            </Form.Item>
          )}

          {message &&
            (hasError
              ? showNotification(
                  simpleErrorNotification({
                    statusCode: 400,
                    message: message,
                  }),
                )
              : showNotification(
                  successNotification({ statusCode: 200, message: message }),
                ))}

          <Form.Item>
            <Button
              onClick={this.handleTestClick}
              loading={testStatus === STATUS.RUNNING}
            >
              Test
            </Button>
          </Form.Item>

          {/* <Form.Item
            label="Type"
            name="type"
            rules={[{ required: false, message: 'Type is required' }]}
          >
            <Select>
              <Select.Option value={FEED_TYPES.CSV}>CSV</Select.Option>
              <Select.Option value={FEED_TYPES.JSON}>JSON</Select.Option>
              <Select.Option value={FEED_TYPES.TXT}>TXT</Select.Option>
              <Select.Option value={FEED_TYPES.STIX}>STIX</Select.Option>
            </Select>
          </Form.Item> */}

          <Form.Item
            label="Threat Type"
            name="threatType"
            rules={[{ required: false, message: 'Threat Type is required' }]}
          >
            <Select>
              <Select.Option value={FEED_THREAT_TYPE.IP}>IP</Select.Option>
              <Select.Option value={FEED_THREAT_TYPE.DOMAIN}>
                Domain
              </Select.Option>
              <Select.Option value={FEED_THREAT_TYPE.URL}>URL</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item className={styles.buttons}>
            <Space>
              <Button onClick={this.props.onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Consume
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    );
  };

  handleTagSelect = (tag) => {
    this.setState({ tag });
    this.props.resetTagSearch();
  };

  renderMappingStep = () => {
    const { initialValues, missingColumns } = this.state;
    const {
      feedTable,
      message,
      saveStatus,
      foundTags,
      searchStatus,
    } = this.props;

    //if (!feedTable.rows?.length) return null;

    let columns;
    let data;
    if (!feedTable.rows?.length) return null;

    if (initialValues?.type !== 'STIX') {
      columns = this.getColumns();
      data = this.getData();
    }
    //const columns = this.getColumns();
    //const data = this.getData();
    const columnNameByCode = {
      feed_name: 'Feed Name',
    };
    const hasError = saveStatus === STATUS.ERROR;

    return (
      <div>
        <h1>Map Feed Fields</h1>

        <Form
          name="basic"
          autoComplete="off"
          initialValues={initialValues}
          onFinish={this.handleFinalFormSubmit}
          className={styles['mapping-form']}
          // onFinishFailed={onFinishFailed}
        >
          {message &&
            hasError &&
            showNotification(
              simpleErrorNotification({ statusCode: 400, message: message }),
            )}
          <div className={styles.inputs}>
            <Form.Item
              label="Feed Name"
              name="feedName"
              rules={[{ required: true, message: 'Feed Name is required' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Vendor"
              name="vendor"
              rules={[{ required: true, message: 'Vendor is required' }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Feed Source Score"
              name="feedCustomScore"
              rules={[
                { required: true, message: 'Feed Source Score is required' },
              ]}
            >
              <InputNumber />
            </Form.Item>
            <Form.Item
              label="Tag"
              name="feedTag"
              rules={[{ required: false, message: 'Tag is required' }]}
            >
              <TagSelect
                foundTags={foundTags}
                searchStatus={searchStatus}
                searchForTags={this.props.searchForTags}
                onSelect={this.handleTagSelect}
              />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input.TextArea />
            </Form.Item>

            <Form.Item
              name="is_global"
              label="Visibility"
              valuePropName="checked"
            >
              <Checkbox className={styles.radio}>Public</Checkbox>
            </Form.Item>
          </div>

          {!!missingColumns.length &&
            showNotification(
              simpleErrorNotification({
                statusCode: 400,
                message: `Some required columns are missing: ${missingColumns
                  .map((c) => columnNameByCode[c])
                  .join(', ')}`,
              }),
            )}

          {initialValues?.type !== 'STIX' ? (
            <Table columns={columns} dataSource={data} pagination={false} />
          ) : (
            ''
          )}
          {/* <Table columns={columns} dataSource={data} pagination={false} /> */}

          <Form.Item className={styles.buttons}>
            <Space>
              <Button onClick={this.props.onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    );
  };

  renderFileUploadMappingStep = () => {
    const { initialValues, missingColumns } = this.state;
    const { feedTable, message, saveStatus } = this.props;
    let columns;
    let data;
    if (!feedTable.rows?.length) return null;

    if (initialValues?.type !== 'STIX') {
      columns = this.getColumns();
      data = this.getData();
    }
    const columnNameByCode = {
      feed_name: 'Feed Name',
    };
    const hasError = saveStatus === STATUS.ERROR;

    return (
      <div>
        <h1>Map Feed Fields</h1>

        <Form
          name="basic"
          autoComplete="off"
          initialValues={initialValues}
          onFinish={this.handleFinalFileUploadFormSubmit}
          className={styles['mapping-form']}
          // onFinishFailed={onFinishFailed}
        >
          {message &&
            hasError &&
            showNotification(
              simpleErrorNotification({ statusCode: 400, message: message }),
            )}
          <div className={styles.inputs}>
            <Form.Item
              label="Feed Name"
              name="feedName"
              rules={[{ required: true, message: 'Feed Name is required' }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Source Score"
              name="custom_score"
              rules={[{ required: true, message: 'Source score is required' }]}
            >
              <InputNumber />
            </Form.Item>
            <Form.Item
              label="Threat Duration (days)"
              name="threat_duration"
              rules={[
                { required: true, message: 'Threat Duration is required' },
              ]}
            >
              <InputNumber />
            </Form.Item>
            <Form.Item
              label="Trust Level"
              name="trust_level"
              rules={[{ required: false, message: 'Trust Level is required' }]}
            >
              <Select>
                <Select.Option value="low">Low</Select.Option>
                <Select.Option value="medium">Medium</Select.Option>
                <Select.Option value="high">High</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              label="Tag"
              name="feedTag"
              rules={[{ required: false, message: 'Tag is required' }]}
            >
              <TagSelect
                foundTags={this.props.foundTags}
                searchStatus={this.props.searchStatus}
                searchForTags={this.props.searchForTags}
                onSelect={this.handleTagSelect}
              />
            </Form.Item>
            <Form.Item label="Description" name="vendor_description">
              <Input.TextArea />
            </Form.Item>
            <Form.Item label="Vendor" name="vendor">
              <Input />
            </Form.Item>
            <Form.Item
              name="is_global"
              label="Visibility"
              valuePropName="checked"
            >
              <Checkbox className={styles.radio}>Public</Checkbox>
            </Form.Item>
          </div>

          {!!missingColumns.length &&
            showNotification(
              simpleErrorNotification({
                statusCode: 400,
                message: `Some required columns are missing: ${missingColumns
                  .map((c) => columnNameByCode[c])
                  .join(', ')}`,
              }),
            )}

          {initialValues?.type !== 'STIX' ? (
            <Table columns={columns} dataSource={data} pagination={false} />
          ) : (
            ''
          )}

          <Form.Item className={styles.buttons}>
            <Space>
              <Button onClick={this.props.onCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Save
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </div>
    );
  };

  render() {
    const {
      showAuthStep,
      showInitialChoiceStep,
      showMappingStep,
      showFileUploadStep,
      showFileUploadMappingStep,
    } = this.state;
    const { consumeStatus } = this.props;
    const isLoading = consumeStatus === STATUS.LOADING;
    return (
      <div className={styles.details}>
        {isLoading && <Spin style={{ margin: 40 }} />}
        {!isLoading && showInitialChoiceStep && this.renderInitialChoiceStep()}
        {!isLoading &&
          !showInitialChoiceStep &&
          showAuthStep &&
          this.renderAuthStep()}
        {!isLoading &&
          !showInitialChoiceStep &&
          !showAuthStep &&
          showMappingStep &&
          this.renderMappingStep()}
        {!isLoading &&
          !showInitialChoiceStep &&
          !showAuthStep &&
          showFileUploadMappingStep &&
          this.renderFileUploadMappingStep()}
        {!isLoading &&
          !showInitialChoiceStep &&
          showFileUploadStep &&
          this.renderFileUploadStep()}
      </div>
    );
  }
}

export default FeedDetails;
